import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import fetchClient from '@eureca/recruiter-api-sdk/client';
import type { GetCompanies200, GetCompaniesQueryParams } from '@eureca/recruiter-api-sdk/types';
import { ProgramsFetchError } from '../programs-list/programs-query';

export interface CompanyOption {
  id: string;
  name: string;
}

/**
 * Typeahead query for the program-creation company picker. Hits `GET /companies`:
 *
 *   - When the typed query is ≥ 2 chars: forwards `?search=<q>` to filter server-side.
 *   - Otherwise (empty / 1 char): omits `search` so the dropdown shows the first
 *     companies the consultor has access to. The BFF rejects `search` shorter than
 *     2 chars (min length on `CompanyListQuerySchema`), so dropping it is required.
 *
 * Keeping the queryKey deduped on `effectiveSearch` means typing "a" doesn't waste
 * a fetch — it reuses the same "no filter" cache entry as the open-on-mount call.
 *
 * `placeholderData: keepPreviousData` keeps the prior list visible while the new
 * query resolves, so the dropdown doesn't blank out on every keystroke.
 *
 * `pageSize: 25` matches the BFF's allowed page sizes (10/25/50/100); the typeahead
 * surfaces 25 rows which is plenty for a Combobox dropdown.
 */
export function companySearchQueryOptions(search: string) {
  const trimmed = search.trim();
  const effectiveSearch = trimmed.length >= 2 ? trimmed : undefined;
  const params: GetCompaniesQueryParams = {
    ...(effectiveSearch ? { search: effectiveSearch } : {}),
    page: 1,
    pageSize: 25,
  };
  return queryOptions({
    queryKey: ['recruiter', 'companies', 'typeahead', effectiveSearch ?? null] as const,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }): Promise<CompanyOption[]> => {
      const res = await fetchClient<GetCompanies200>({
        method: 'GET',
        url: '/companies',
        params,
        signal,
      });
      if (res.error) {
        throw new ProgramsFetchError(
          `Failed to load companies (status ${res.error.status})`,
          res.error.status
        );
      }
      return res.data.items.map((c) => ({ id: c.id, name: c.name }));
    },
  });
}
